import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { PText } from "@porsche-design-system/components-react"
import {
	breakpoint,
	spacingFluidSm,
	spacingFluidXl,
	spacingStaticLg,
	spacingStaticMd,
} from "@porsche-design-system/components-react/emotion"
import type { MouseEvent } from "react"
import { useCallback, useEffect, useMemo } from "react"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { useLocation, useNavigate } from "react-router"
import { getPcw20Host } from "src/common/getPcw20Host"
import { dealerAppRoute } from "src/common/routes"
import { useDealerAppRoutes } from "src/common/routes/useDealerAppRoutes"
import { AppTitle, ContainerHeader, ContentWrapper, Main } from "src/components"
import { trackEvent, trackPageView } from "src/components/adobeAnalytics"
import { useConfig } from "src/hooks"
import type { HomeLinksGridProps } from "./components/HomeLinksGrid"
import { HomeLinksGrid } from "./components/HomeLinksGrid"

const messages = defineMessages({
	welcomeSubtitle: {
		defaultMessage: "What do you want to do today?",
		id: "home.welcomeSubtitle",
	},
	welcomeTitleEvening: {
		defaultMessage: "Good evening, {name}.",
		id: "home.welcomeTitleEvening",
	},
	welcomeTitleMidday: {
		defaultMessage: "Good day, {name}.",
		id: "home.welcomeTitleMidday",
	},
	welcomeTitleMorning: {
		defaultMessage: "Good morning, {name}.",
		id: "home.welcomeTitleMorning",
	},
})

export const HomeContainer = () => {
	const { dealershipId, dealershipInfo, displayName, environment, locale, marketplaceKey, permissions, username } =
		useDealerContext()
	const { formatMessage } = useIntl()
	const config = useConfig()
	const location = useLocation()
	const navigate = useNavigate()
	const routes = useDealerAppRoutes()

	useEffect(() => {
		if (location.search) {
			// clean url query params from previously bookmarked inventory list with filters
			navigate(location.pathname, { replace: true })
		}
		trackPageView("home")
	}, [location, navigate])

	const welcomeMessageKey = useMemo<Extract<keyof typeof messages, `welcomeTitle${string}`>>(() => {
		const hour = new Date().getHours() // returns 0-23
		if (hour >= 5 && hour < 12) {
			return "welcomeTitleMorning" // 5:00 - 11:59 AM
		}
		if (hour >= 12 && hour < 18) {
			return "welcomeTitleMidday" // 12:00 - 5:59 PM
		}
		return "welcomeTitleEvening" // 6:00 PM - 4:59 AM
	}, [])

	const basename = dealerAppRoute.url(dealershipId, locale)
	const hrefs: HomeLinksGridProps["hrefs"] = useMemo(() => {
		// since all links are rendered as regular `a` tags, not react-router `Link`s, we need to take care of the basename
		// ourselves so that control + click, right click etc. still work as usual
		// TODO: this is only genuinely needed for the truly external targets (configurator/pcss/pcw/pcwCms).
		// buildsheets/vehicleInformation/inventoryList are same-app, same-dealership routes and could use a
		// real <Link> instead (see NotificationSettingsContainer.tsx's `<PLink><Link to={...}></PLink>`
		// pattern), removing the need for onLinkClick's manual modifier-key handling for those targets.
		const buildsheetsUrl = routes?.buildsheets?.url()
		const vehicleInformationUrl = routes?.vehicleInformation?.url()
		const inventoryListUrl = routes?.inventoryList?.url()
		const pcw20Host = getPcw20Host(dealershipId)

		return {
			...(config.vehicleInformationEnabled
				? { vehicleInformation: vehicleInformationUrl ? `${basename}${vehicleInformationUrl}` : undefined }
				: { buildsheets: buildsheetsUrl ? `${basename}${buildsheetsUrl}` : undefined }),
			configurator: routes?.external?.configurator?.url({
				backActionUrl: window.location.href,
				canReportSales: permissions.canReportSales === true,
				dealership: dealershipId,
				environment,
				locale,
			}),
			inventoryList: inventoryListUrl ? `${basename}${inventoryListUrl}` : undefined,
			pcss: routes?.external?.pcss.url(),
			...(pcw20Host && {
				pcw: `https://${pcw20Host}`,
				pcwCms:
					marketplaceKey === "de"
						? "https://app.storyblok.com/#/me/spaces/293255674717942/stories/0/0/index/0"
						: "https://app.storyblok.com/#/me/spaces/329189/stories/0/0/index/0",
			}),
		}
	}, [
		basename,
		config.vehicleInformationEnabled,
		dealershipId,
		environment,
		locale,
		permissions.canReportSales,
		routes,
		marketplaceKey,
	])

	const onLinkClick = useCallback(
		(target: keyof HomeLinksGridProps["hrefs"], e: MouseEvent) => {
			// Let the browser handle right-click, middle-click, ctrl/meta-click
			if (
				e.defaultPrevented ||
				e.button !== 0 || // Only left click
				e.metaKey || // Cmd on Mac
				e.ctrlKey || // Ctrl on Windows/Linux
				e.altKey ||
				e.shiftKey
			) {
				return
			}

			const url = hrefs[target]
			if (url?.startsWith("/")) {
				e.preventDefault()
				navigate(url.replace(basename, ""))
			}

			trackEvent({ action: `click_on_${target}`, category: "home" })
		},
		[basename, hrefs, navigate],
	)

	return (
		<Main>
			<StyledContentWrapper>
				<AppTitle locationCode={dealershipInfo.locationCode} />
				<ContainerHeader title={formatMessage(messages[welcomeMessageKey], { name: displayName ?? username })}>
					<Subline>
						<FormattedMessage {...messages.welcomeSubtitle} />
					</Subline>
				</ContainerHeader>

				<HomeLinksGrid hrefs={hrefs} onLinkClick={onLinkClick} />
			</StyledContentWrapper>
		</Main>
	)
}

const Subline = styled(PText)`
    margin: -${spacingStaticLg} 0 ${spacingStaticMd};
`

const StyledContentWrapper = styled(ContentWrapper)`
    padding-bottom: ${spacingFluidSm};

    @media (min-width: ${breakpoint.xl}px) {
        padding-bottom: ${spacingFluidXl};
    }
`
